import {
  DeliveryAreaCreateRequestType,
  DeliveryAreaGetResponseType,
  DeliveryAreaDayType,
  CustomerZoneListResponseType,
} from "@sellar-io/api-v2/types";
import { capitalize, getCoverageMismatches } from "@sellar-io/common";
import { forwardRef, useImperativeHandle } from "react";
import dayjs from "dayjs";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";
import { Icon } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { BadgeGrey } from "../../components/badge/Badge";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../components/modal/Dialog";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
} from "../../components/table/TableLegacy";
import {
  DELIVERY_CHARGES,
  DELIVERY_SCHEDULES,
  DELIVERY_ZONES,
} from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useDeliveryAreaDelete } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaDelete";
import { useDeliveryAreaUpdate } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaUpdate";
import { useDeliveryAreaCreateContext } from "../../hooks/useDeliveryAreaCreateContext.shared";
import { PostcodeMoveDialog } from "../../components/postcode/PostcodeMoveDialog";
import { isPostcodeConflictError } from "../../utils/postcodeErrorHelper";
import { useCustomerZonePostcodesMove } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesMove";
import { DeliveryAreaFormTableRow } from "./DeliveryAreaFormTableRow";
import { DeliveryDaysInput } from "./DeliveryDaysInput";
import { ReactHookFormSwitch } from "../../components/form/ReactHookFormSwitch";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import { useCustomerZonePostcodesAdd } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesAdd";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  InputDate,
  ListItemIconTile,
  SingleFieldDialog,
  InputTime,
  InputNumber,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { expandPostcodeRange } from "../../utils/expandPostcodeRange";
import { useCustomerZonesList } from "~/hooks/api/v2/customerZones/useCustomerZonesList";
import { useCustomerZonePostcodesCoverage } from "~/hooks/api/v2/customerZones/useCustomerZonePostcodesCoverage";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { deliveryDays } from "./DeliveryAreaForm.shared";
import { groupPostcodes } from "../delivery/groupPostcodes";

export type DeliveryAreaFormRef = {
  submitForm: () => void;
  isDirty: () => boolean;
};

export const DeliveryAreaForm = forwardRef<
  DeliveryAreaFormRef,
  {
    onSubmit: (
      data: Omit<DeliveryAreaCreateRequestType, "postcodes" | "name"> & {
        name: string | undefined;
      },
      options:
        | {
            createAnother: boolean;
            redirectToCustomerZones: boolean;
          }
        | undefined,
    ) => void;
    postcodeError: boolean;
    deliveryArea?: DeliveryAreaGetResponseType;
    hideActions?: boolean;
  }
>(({ onSubmit, postcodeError, deliveryArea, hideActions = false }, ref) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { postcodes, selectedDays, setPostcodes, setSelectedDays } =
    useDeliveryAreaCreateContext();
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [invalidPostcode, setInvalidPostcode] = useState(false);
  const [editingPostcodesForId, setEditingPostcodesForId] = useState<
    number | null
  >(null);
  const isEditingPostcodes = editingPostcodesForId === deliveryArea?.id;
  const [postcodeRemoveTarget, setPostcodeRemoveTarget] = useState<{
    postcodeComponent: string;
    index: number;
  } | null>(null);
  const deleteDeliveryAreaMutation = useDeliveryAreaDelete(
    deliveryArea?.id ?? 0,
  );

  const inlineDeliveryAreaUpdateMutation = useDeliveryAreaUpdate(
    deliveryArea?.id ?? 0,
  );
  const [inlinePostcodesSaveError, setInlinePostcodesSaveError] = useState<
    string | null
  >(null);

  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const deliveryZoneMode = useDeliveryZoneMode();
  const showManualZoneAssistance =
    unbundlingEnabled && !deliveryZoneMode.isBasicMode;

  const showPostcodeEditor =
    !unbundlingEnabled || !deliveryArea || isEditingPostcodes;

  const { business } = useSellarCurrentBusiness();
  const supplierId = business?.Supplier?.id;
  const supplier = useSupplier(supplierId ?? 0, {
    enabled: unbundlingEnabled && !!supplierId,
  });

  const formRef = useRef<HTMLFormElement>(null);

  // Store initial values to track dirtiness
  const initialValuesRef = useRef({
    name: deliveryArea?.name,
    postcodes:
      deliveryArea?.DeliveryAreaPostcodes?.map((p) => p.postcodeComponent) ??
      [],
    selectedDays: deliveryArea?.DeliveryAreaDays?.map((d) => d.day) ?? [],
    weeklyCadence: deliveryArea?.weeklyCadence ?? 1,
    startAt: deliveryArea?.startAt ?? dayjs().add(1, "day").toDate(),
    orderCutOffDaysBefore: deliveryArea?.orderCutOffDaysBefore ?? null,
    orderCutOffTime: deliveryArea?.orderCutOffTime
      ? `${dayjs(deliveryArea.orderCutOffTime)
          .subtract(1, "hour")
          .get("hour")}:${
          dayjs(deliveryArea.orderCutOffTime).get("minutes") === 0
            ? "00"
            : dayjs(deliveryArea.orderCutOffTime).get("minutes")
        }`
      : null,
    includeWeekend: deliveryArea?.includeWeekend ?? true,
  });

  const updateInitialValues = useEffectEvent(() => {
    if (deliveryArea) {
      initialValuesRef.current = {
        name: deliveryArea.name,
        postcodes:
          deliveryArea.DeliveryAreaPostcodes?.map((p) => p.postcodeComponent) ??
          [],
        selectedDays: deliveryArea.DeliveryAreaDays?.map((d) => d.day) ?? [],
        weeklyCadence: deliveryArea.weeklyCadence ?? 1,
        startAt: deliveryArea.startAt ?? dayjs().add(1, "day").toDate(),
        orderCutOffDaysBefore: deliveryArea.orderCutOffDaysBefore ?? null,
        orderCutOffTime: deliveryArea.orderCutOffTime
          ? `${dayjs(deliveryArea.orderCutOffTime)
              .subtract(1, "hour")
              .get("hour")}:${
              dayjs(deliveryArea.orderCutOffTime).get("minutes") === 0
                ? "00"
                : dayjs(deliveryArea.orderCutOffTime).get("minutes")
            }`
          : null,
        includeWeekend: deliveryArea.includeWeekend ?? true,
      };
    }
  });

  // Update initial values when deliveryArea changes (for update scenario)
  useEffect(() => {
    updateInitialValues();
  }, [deliveryArea?.id]);

  const [createAnother, setCreateAnother] = useState(false);

  const customerZonePostcodeComponents = supplier.data
    ? supplier.data.CustomerZonePostcodes.map((p) => p.postcodeComponent)
    : [];

  const deliveryAreaPostcodesNotCoveredByCustomerZones =
    showManualZoneAssistance &&
    customerZonePostcodeComponents.length &&
    postcodes.length
      ? getCoverageMismatches({
          leftPostcodeComponents: postcodes,
          rightPostcodeComponents: customerZonePostcodeComponents,
        }).leftNotCoveredByRight
      : [];

  const uncoveredCount = deliveryAreaPostcodesNotCoveredByCustomerZones.length;
  const uncoveredPostcodesLabel =
    uncoveredCount > 3
      ? `${deliveryAreaPostcodesNotCoveredByCustomerZones.slice(0, 3).join(", ")} (+${uncoveredCount - 3} more)`
      : deliveryAreaPostcodesNotCoveredByCustomerZones.join(", ");
  const isOrAre = uncoveredCount === 1 ? "is" : "are";
  const thisOrThese = uncoveredCount === 1 ? "this" : "these";
  const itIsOrTheyAre = uncoveredCount === 1 ? "it is" : "they're";

  const [customerZoneDialogPostcode, setCustomerZoneDialogPostcode] = useState<
    string | null
  >(null);

  const [wasBasicMode, setWasBasicMode] = useState(
    deliveryZoneMode.isBasicMode,
  );
  if (wasBasicMode !== deliveryZoneMode.isBasicMode) {
    setWasBasicMode(deliveryZoneMode.isBasicMode);
    setCustomerZoneDialogPostcode(null);
    setPostcodeRemoveTarget(null);
  }

  const customerZonePostcodesCoverageMutation =
    useCustomerZonePostcodesCoverage();

  const redirectToCustomerZones =
    (state?.redirectToCustomerZones as boolean | undefined) ?? false;

  const syncDeliveryAreaContext = useEffectEvent(() => {
    setPostcodes(
      deliveryArea?.DeliveryAreaPostcodes.map(
        (deliveryAreaPostcode) => deliveryAreaPostcode.postcodeComponent,
      ) ?? [],
    );

    if (deliveryArea?.DeliveryAreaDays) {
      setSelectedDays(
        deliveryArea.DeliveryAreaDays.map(
          (deliveryAreaDay) => deliveryAreaDay.day as DeliveryAreaDayType,
        ),
      );
    }
  });

  useEffect(() => {
    syncDeliveryAreaContext();
  }, [deliveryArea?.id]);

  const form = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      name: deliveryArea?.name,
      deliveryDays: selectedDays,
      postcode: "",
      weeklyCadence: deliveryArea?.weeklyCadence ?? 1,
      startAt: deliveryArea?.startAt ?? dayjs().add(1, "day").toDate(),
      orderCutOffDaysBefore: deliveryArea?.orderCutOffDaysBefore ?? null,
      orderCutOffTime: deliveryArea?.orderCutOffTime
        ? `${dayjs(deliveryArea.orderCutOffTime)
            .subtract(1, "hour")
            .get("hour")}:${
            dayjs(deliveryArea.orderCutOffTime).get("minutes") === 0
              ? "00"
              : dayjs(deliveryArea.orderCutOffTime).get("minutes")
          }`
        : null,
      includeWeekend: deliveryArea?.includeWeekend ?? true,
    },
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formRef.current?.requestSubmit();
    },
    isDirty: () => {
      const currentValues = form.getValues();
      const initial = initialValuesRef.current;

      // Check form fields
      // For new delivery areas, allow closing if name is empty (treat as unchanged)
      const isNew = !deliveryArea;
      const currentName = currentValues.name?.trim() ?? "";
      const initialName = initial.name?.trim() ?? "";
      if (currentName !== initialName && !(isNew && currentName === ""))
        return true;
      if ((currentValues.weeklyCadence ?? 1) !== initial.weeklyCadence)
        return true;
      if (
        dayjs(currentValues.startAt).format("YYYY-MM-DD") !==
        dayjs(initial.startAt).format("YYYY-MM-DD")
      )
        return true;
      if (
        (currentValues.orderCutOffDaysBefore ?? null) !==
        initial.orderCutOffDaysBefore
      )
        return true;
      if ((currentValues.orderCutOffTime ?? null) !== initial.orderCutOffTime)
        return true;
      if ((currentValues.includeWeekend ?? true) !== initial.includeWeekend)
        return true;

      // Check context values (postcodes and selectedDays)
      if (postcodes.length !== initial.postcodes.length) return true;
      if (postcodes.some((p, i) => p !== initial.postcodes[i])) return true;

      if (selectedDays.length !== initial.selectedDays.length) return true;
      if (selectedDays.some((d, i) => d !== initial.selectedDays[i]))
        return true;

      return false;
    },
  }));

  const orderCutOffDaysBeforeValue = useWatch({
    control: form.control,
    name: "orderCutOffDaysBefore",
  });
  const orderCutOffTimeValue = useWatch({
    control: form.control,
    name: "orderCutOffTime",
  });
  const includeWeekendValue = useWatch({
    control: form.control,
    name: "includeWeekend",
  });
  const startAtValue = useWatch({
    control: form.control,
    name: "startAt",
  });
  const hasOrderCutOff = !!orderCutOffTimeValue && !!orderCutOffDaysBeforeValue;

  useEffect(() => {
    if (selectedDays.length) {
      if (!orderCutOffTimeValue && !deliveryArea?.orderCutOffTime) {
        form.setValue("orderCutOffTime", "23:59");
      }
      if (!orderCutOffDaysBeforeValue) {
        form.setValue("orderCutOffDaysBefore", 1);
      }
      return;
    }
    form.setValue(
      "orderCutOffDaysBefore",
      deliveryArea?.orderCutOffDaysBefore ?? null,
    );
    form.setValue(
      "orderCutOffTime",
      deliveryArea?.orderCutOffTime
        ? `${dayjs(deliveryArea.orderCutOffTime)
            .subtract(1, "hour")
            .get("hour")}:${
            dayjs(deliveryArea.orderCutOffTime).get("minutes") === 0
              ? "00"
              : dayjs(deliveryArea.orderCutOffTime).get("minutes")
          }`
        : null,
    );
  }, [
    selectedDays,
    orderCutOffTimeValue,
    orderCutOffDaysBeforeValue,
    deliveryArea?.orderCutOffDaysBefore,
    deliveryArea?.orderCutOffTime,
    form,
  ]);

  const checkPostcodeValidCharacters = (postcode: string) => {
    if (/[^a-zA-Z0-9]/.test(postcode.replaceAll(" ", ""))) return false;
    if (postcode.length < 3) return true; // Return true if string is shorter than 3 characters
    return [...postcode.trim()]
      .slice(0, 3)
      .some((char) => !isNaN(Number(char)) && char !== " ");
  };

  const handleSubmit = form.handleSubmit((data) =>
    onSubmit(
      {
        ...data,
        orderCutOffTime: data.orderCutOffTime
          ? dayjs()
              .set("hour", parseInt(data.orderCutOffTime.split(":")[0] ?? ""))
              .set("minute", parseInt(data.orderCutOffTime.split(":")[1] ?? ""))
              .toDate()
          : null,
      },
      {
        redirectToCustomerZones,
        createAnother,
      },
    ),
  );

  const savePostcodesInline = (nextPostcodes: string[]) => {
    setPostcodes(nextPostcodes);

    if (!unbundlingEnabled) return;
    if (!deliveryArea) return;

    setInlinePostcodesSaveError(null);

    inlineDeliveryAreaUpdateMutation.mutate(
      {
        postcodes: nextPostcodes.map((pc) => pc.trim().toUpperCase()),
      },
      {
        onError: (e) => {
          const assertedError = apiV2AssertError(e);
          setInlinePostcodesSaveError(
            assertedError?.response?.data.message ??
              "Unable to update delivery area postcodes",
          );
        },
      },
    );
  };

  return (
    <>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={"flex flex-col gap-4"}
      >
        <Card className="bg-white">
          {!unbundlingEnabled && (
            <CardHeader className="flex flex-col bg-white p-4">
              <H5>Delivery area {orderCutOffTimeValue?.valueOf()}</H5>
            </CardHeader>
          )}
          <CardContent className={"flex flex-col gap-2 p-4"}>
            <InputLabel>Name</InputLabel>
            <InputContainer>
              <InputText {...form.register("name", { required: true })} />
            </InputContainer>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader className="flex flex-col bg-white p-4">
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <H5>Postcodes</H5>
              {unbundlingEnabled && deliveryArea && (
                <Button
                  type="button"
                  color="secondary"
                  size="small"
                  className="mb-2"
                  disabled={inlineDeliveryAreaUpdateMutation.isPending}
                  onClick={() => {
                    setEditingPostcodesForId(
                      isEditingPostcodes ? null : deliveryArea.id,
                    );
                    form.setValue("postcode", "");
                    setInvalidPostcode(false);
                  }}
                >
                  {isEditingPostcodes ? "Done editing" : "Edit postcodes"}
                </Button>
              )}
            </div>
            <H6>
              Define 1 or more postcode areas (e.g. BS), postcode districts
              (e.g. BS1), postcode sectors (e.g. BS1 6) or a combination of all
              three. You can also enter a range (e.g. S1-9) to add multiple
              districts at once. Enter each one and select 'add'. You can add as
              many as you like.
            </H6>
            {deliveryZoneMode.isBasicMode && (
              <Alert
                type="information"
                message={
                  <strong>
                    The postcodes you list here make up your Local default
                    delivery zone
                  </strong>
                }
                className="mt-2 w-full px-3 py-1"
              />
            )}
          </CardHeader>
          <CardContent className={"flex flex-col gap-2 p-4"}>
            {!unbundlingEnabled && (
              <InputLabel>Postcode areas and/or districts</InputLabel>
            )}
            {postcodeError && !postcodes.length && (
              <Alert
                message={"You must add at least one postcode"}
                type={"error"}
              />
            )}

            {showPostcodeEditor && (
              <div className="flex gap-4">
                <InputContainer className="w-full">
                  <InputText
                    className="w-full"
                    {...form.register("postcode", { required: false })}
                    placeholder="e.g. BS1 or S1-9"
                  />
                </InputContainer>
                <Button
                  onClick={() => {
                    setInvalidPostcode(false);
                    const postcode = form.getValues("postcode");
                    if (postcode) {
                      const expandedPostcodes = expandPostcodeRange(postcode);

                      if (
                        expandedPostcodes.some(
                          (pc) => !checkPostcodeValidCharacters(pc),
                        )
                      ) {
                        return setInvalidPostcode(true);
                      }

                      const postcodesToAdd = expandedPostcodes.map((pc) =>
                        pc.trim().toUpperCase(),
                      );

                      const nextPostcodes = postcodes.length
                        ? [...postcodes, ...postcodesToAdd]
                        : [...postcodesToAdd];

                      savePostcodesInline(nextPostcodes);

                      form.setValue("postcode", "");

                      if (!showManualZoneAssistance) return;
                      customerZonePostcodesCoverageMutation.mutate(
                        {
                          postcodeComponents: postcodesToAdd,
                        },
                        {
                          onSuccess: (response) => {
                            const coverage = response.coverage[0];
                            if (coverage && !coverage.isCoveredByCustomerZone) {
                              setCustomerZoneDialogPostcode(
                                coverage.postcodeComponent,
                              );
                            }
                          },
                        },
                      );
                    }
                  }}
                >
                  <Icon icon={"PLUS"} />
                  Add
                </Button>
              </div>
            )}
            {!!deliveryAreaPostcodesNotCoveredByCustomerZones.length && (
              <Alert
                type={"warning"}
                title={`${uncoveredPostcodesLabel} ${isOrAre} not listed in a delivery zone`}
                message={`This means that customers in ${thisOrThese} postcodes will not see a direct delivery option on your storefront, even if ${itIsOrTheyAre} listed as part of this delivery schedule`}
                className="w-full"
              >
                <Button
                  color="secondary"
                  className="border border-orange-800 bg-transparent text-orange-800"
                  onClick={() => {
                    setCustomerZoneDialogPostcode(
                      deliveryAreaPostcodesNotCoveredByCustomerZones[0] ?? null,
                    );
                  }}
                >
                  ADD TO A DELIVERY ZONE
                </Button>
              </Alert>
            )}
            {invalidPostcode && (
              <Alert
                title={"Unable to save postcode"}
                message={
                  "Please enter either a postcode area (such as BS), postcode district (such as BS1) or postcode sector (such as BS1 6)."
                }
              />
            )}

            {inlinePostcodesSaveError && (
              <Alert type="error" message={inlinePostcodesSaveError} />
            )}

            {!showPostcodeEditor ? (
              postcodes.length === 0 ? (
                <p>No postcodes added</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {groupPostcodes(postcodes).map((group) => (
                    <li key={group.area}>{group.postcodes.join(", ")}</li>
                  ))}
                </ul>
              )
            ) : unbundlingEnabled ? (
              <div className="flex flex-col gap-2">
                {Object.entries(
                  postcodes.reduce<
                    Record<string, { postcode: string; index: number }[]>
                  >((acc, postcode, i) => {
                    const area =
                      postcode.match(/^[A-Za-z]+/)?.[0].toUpperCase() ??
                      postcode;
                    (acc[area] ??= []).push({ postcode, index: i });
                    return acc;
                  }, {}),
                )
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([area, items]) => (
                    <div key={area} className="flex flex-wrap gap-2">
                      {items.map(({ postcode, index: i }) => (
                        <DataButton
                          key={postcode}
                          type="button"
                          border={true}
                          className={twMerge(
                            deliveryAreaPostcodesNotCoveredByCustomerZones.includes(
                              postcode,
                            ) &&
                              "border-orange-300 bg-orange-100 text-orange-800",
                          )}
                          onClick={() => {
                            if (deliveryZoneMode.isBasicMode) {
                              savePostcodesInline(
                                postcodes.filter((_, index) => index !== i),
                              );
                              return;
                            }
                            setPostcodeRemoveTarget({
                              postcodeComponent: postcode,
                              index: i,
                            });
                          }}
                        >
                          <span className="flex items-center gap-2">
                            {deliveryAreaPostcodesNotCoveredByCustomerZones.includes(
                              postcode,
                            ) ? (
                              <Icon icon="ALERT" size={0.6} />
                            ) : (
                              ""
                            )}
                            {postcode}
                            <Icon icon="CLOSE" size={0.6} />
                          </span>
                        </DataButton>
                      ))}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {postcodes.map((postcode, i) => (
                  <BadgeGrey
                    icon={"CLOSE"}
                    iconPosition={"right"}
                    key={i}
                    className="cursor-pointer"
                    onClick={() =>
                      setPostcodes(postcodes.filter((_, index) => index !== i))
                    }
                  >
                    {postcode}
                  </BadgeGrey>
                ))}
              </div>
            )}

            {showManualZoneAssistance && (
              <DeliveryAreaPostcodeRemoveDialog
                target={postcodeRemoveTarget}
                onDismiss={() => setPostcodeRemoveTarget(null)}
                postcodes={postcodes}
                currentDeliveryAreaId={deliveryArea?.id}
                onRemoveIndex={(index) => {
                  const nextPostcodes = postcodes.filter((_, i) => i !== index);
                  savePostcodesInline(nextPostcodes);
                }}
              />
            )}

            {showManualZoneAssistance && (
              <CustomerZoneDialog
                active={customerZoneDialogPostcode !== null}
                postcode={customerZoneDialogPostcode}
                onDismiss={() => setCustomerZoneDialogPostcode(null)}
              />
            )}
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
            <H5>Schedule</H5>
            <H6>
              When do you deliver to these postcodes? You can leave this blank
              if you deliver on an ad-hoc basis and don't operate to a schedule.
            </H6>
          </CardHeader>
          <CardContent className={"flex flex-col gap-2 p-4"}>
            <DeliveryDaysInput
              selectedDays={selectedDays}
              setSelectedDays={setSelectedDays}
              label={"Delivery days"}
              deliveryDays={deliveryDays}
            />
            <InputLabel>Deliver every...</InputLabel>
            <div className="flex flex-row items-center gap-2">
              <InputContainer>
                <InputNumber
                  {...form.register("weeklyCadence", {
                    required: !!selectedDays.length,
                    disabled: !selectedDays.length,
                  })}
                  min={0}
                  disabled={!selectedDays.length}
                />
              </InputContainer>
              {form.getValues("weeklyCadence") ? <p>weeks</p> : <p>week(s)</p>}
            </div>
            <InputLabel>Starting on</InputLabel>
            <InputContainer>
              <InputDate
                value={dayjs(startAtValue).format("YYYY-MM-DD")}
                {...form.register("startAt", {
                  required: true,
                })}
                min={dayjs().format("YYYY-MM-DD")}
              />
            </InputContainer>
          </CardContent>
        </Card>
        {!!selectedDays.length && (
          <Card className="bg-white">
            <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
              <H5>Order cut-off time</H5>
              <H6>
                Let your customers know when they need to order by to guarantee
                their delivery date. Note: You will always be able to adjust the
                delivery date when confirming the order.
              </H6>
            </CardHeader>
            <CardContent className={"flex grid gap-2 p-4 md:grid-cols-2"}>
              <>
                <div className="md:col-span-1">
                  <InputLabel>Place order...</InputLabel>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <InputContainer className={"flex"}>
                      <InputNumber
                        {...form.register("orderCutOffDaysBefore", {
                          required: !!orderCutOffTimeValue,
                          valueAsNumber: true,
                        })}
                        min={0}
                      />
                    </InputContainer>
                    <p>...days before delivery date, and...</p>
                  </div>
                </div>
                <div className="md:col-span-1">
                  <InputLabel>Before</InputLabel>
                  <InputContainer>
                    <InputTime
                      {...form.register("orderCutOffTime", {
                        required: !!orderCutOffDaysBeforeValue,
                      })}
                    />
                  </InputContainer>
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <InputLabel>Include weekend</InputLabel>
                  <div className="flex items-center gap-4 px-2">
                    <ReactHookFormSwitch
                      control={form.control}
                      name="includeWeekend"
                    />
                    <span>
                      Weekends will be{" "}
                      <strong>
                        {includeWeekendValue ? "included" : "excluded"}
                      </strong>{" "}
                      in the order cutoff calculation
                    </span>
                  </div>
                </div>
                {hasOrderCutOff && !!selectedDays.length && (
                  <TableLegacyContainer className={"mt-2 w-full md:col-span-2"}>
                    <TableLegacy className={"w-full"}>
                      <TableLegacyTHead>
                        <TableLegacyTR>
                          <TableLegacyTH>Delivery date</TableLegacyTH>
                          <TableLegacyTH>Order cut-off time</TableLegacyTH>
                        </TableLegacyTR>
                      </TableLegacyTHead>
                      <TableLegacyTBody>
                        {selectedDays.map((day) => (
                          <>
                            <DeliveryAreaFormTableRow
                              day={day}
                              orderCutOffDaysBeforeValue={
                                orderCutOffDaysBeforeValue
                              }
                              orderCutOffTimeValue={orderCutOffTimeValue}
                              includeWeekend={includeWeekendValue}
                            />
                          </>
                        ))}
                      </TableLegacyTBody>
                    </TableLegacy>
                  </TableLegacyContainer>
                )}
              </>
            </CardContent>
          </Card>
        )}
        {!hideActions && (
          <div className="flex justify-end gap-2">
            {!!deliveryArea ? (
              <Button
                color="red"
                type={"button"}
                onClick={() => setDeleteDialogActive(true)}
                className="mr-4"
              >
                Delete
              </Button>
            ) : (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setCreateAnother(true);
                  setTimeout(() => {
                    formRef.current?.requestSubmit();
                  }, 0);
                }}
                color="secondary"
              >
                Save & create another
              </Button>
            )}
            <Button type={"submit"}>Save delivery area</Button>
          </div>
        )}
      </form>
      <Dialog
        active={deleteDialogActive}
        onDismiss={() => setDeleteDialogActive(false)}
      >
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setDeleteDialogActive(false)}
          title={`Delete ${deliveryArea?.name}?`}
          description={`Are you sure you wish to delete this delivery area?`}
        />
        <DialogContent>
          {deleteDeliveryAreaMutation.isError && (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(deleteDeliveryAreaMutation.error)?.response
                  ?.data.message ?? "Unknown error occurred"
              }
            />
          )}
        </DialogContent>
        <DialogCTA>
          <Button
            color={"red"}
            loading={deleteDeliveryAreaMutation.isPending}
            onClick={() =>
              deleteDeliveryAreaMutation.mutate(undefined, {
                onSuccess: () => {
                  if (unbundlingEnabled) {
                    navigate(DELIVERY_SCHEDULES);
                  } else {
                    navigate(DELIVERY_CHARGES);
                  }
                },
              })
            }
          >
            Delete
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
});

DeliveryAreaForm.displayName = "DeliveryAreaForm";

const DeliveryAreaPostcodeRemoveDialog = ({
  target,
  onDismiss,
  postcodes,
  currentDeliveryAreaId,
  onRemoveIndex,
}: {
  target: {
    postcodeComponent: string;
    index: number;
  } | null;
  onDismiss: () => void;
  postcodes: string[];
  currentDeliveryAreaId: number | undefined;
  onRemoveIndex: (index: number) => void;
}) => {
  const customerZonesQuery = useCustomerZonesList(
    {},
    { enabled: target !== null },
  );
  const customerZones = customerZonesQuery.data?.pages.flat() ?? [];

  const deliveryAreaListQuery = useDeliveryAreaList(
    { limit: 50 },
    { enabled: target !== null },
  );
  const allDeliveryAreas = deliveryAreaListQuery.data?.pages.flat() ?? [];

  const postcodeComponent = target?.postcodeComponent ?? null;
  const customerZoneForPostcode: CustomerZoneListResponseType[number] | null =
    postcodeComponent === null
      ? null
      : (customerZones.find((cz) =>
          cz.CustomerZonePostcodes.some(
            (pc) =>
              pc.postcodeComponent.trim().toUpperCase() ===
              postcodeComponent.trim().toUpperCase(),
          ),
        ) ?? null);

  const otherSchedulesContainPostcode =
    postcodeComponent !== null &&
    allDeliveryAreas.some(
      (da) =>
        da.id !== currentDeliveryAreaId &&
        da.DeliveryAreaPostcodes.some(
          (pc) =>
            pc.postcodeComponent.trim().toUpperCase() ===
            postcodeComponent.trim().toUpperCase(),
        ),
    );

  const queriesResolved =
    (customerZonesQuery.isSuccess || customerZonesQuery.isError) &&
    (deliveryAreaListQuery.isSuccess || deliveryAreaListQuery.isError);

  useEffect(() => {
    if (!target) return;
    if (!queriesResolved) return;

    if (customerZoneForPostcode && !otherSchedulesContainPostcode) return;

    onRemoveIndex(target.index);
    onDismiss();
  }, [
    customerZoneForPostcode,
    onDismiss,
    onRemoveIndex,
    otherSchedulesContainPostcode,
    postcodes,
    queriesResolved,
    target,
  ]);

  const handleRemovePostcode = () => {
    if (!target) return;
    onRemoveIndex(target.index);
    onDismiss();
  };

  return (
    <SingleFieldDialog
      active={
        !!target && !!customerZoneForPostcode && !otherSchedulesContainPostcode
      }
      onDismiss={onDismiss}
      size="large"
    >
      <SingleFieldDialogHeader
        title={`Are you sure you want to delete '${postcodeComponent ?? ""}' postcode?`}
        description={`'${postcodeComponent ?? ""}' is listed in your '${customerZoneForPostcode?.name ?? "Local"}' delivery zone. Customers located in this zone will no longer see delivery date information from this delivery schedule`}
        icon="DELETE"
        iconClassName="bg-red-50 text-red-600"
      />

      <SingleFieldDialogCTA>
        <Button color="transparent" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button color="red" disabled={!target} onClick={handleRemovePostcode}>
          Remove
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const CustomerZoneDialog = (props: {
  active: boolean;
  postcode: string | null;
  onDismiss: () => void;
}) => (
  <CustomerZoneDialogContent
    key={`${props.active}:${props.postcode ?? ""}`}
    {...props}
  />
);

const CustomerZoneDialogContent = ({
  active,
  postcode,
  onDismiss,
}: {
  active: boolean;
  postcode: string | null;
  onDismiss: () => void;
}) => {
  const navigate = useNavigate();
  const [selectedCustomerZoneId, setSelectedCustomerZoneId] = useState<
    number | null
  >(null);

  // Move dialog state
  const [moveDialogActive, setMoveDialogActive] = useState(false);
  const [moveTarget, setMoveTarget] = useState<{
    postcode: string;
    fromZoneId: number;
    fromZoneName: string;
  } | null>(null);

  const customerZonesQuery = useCustomerZonesList({ limit: 100 });

  const customerZones = (customerZonesQuery.data?.pages.flat() ?? []).filter(
    (cz) => cz.type === "direct",
  );

  const addCustomerZonePostcodesMutation = useCustomerZonePostcodesAdd(
    selectedCustomerZoneId ?? 0,
  );

  const movePostcodesMutation = useCustomerZonePostcodesMove(
    selectedCustomerZoneId ?? 0,
  );

  const onConfirm = async () => {
    const postcodeToAdd = postcode?.trim().toUpperCase();
    if (!postcodeToAdd) return;
    if (selectedCustomerZoneId === null) return;

    try {
      await addCustomerZonePostcodesMutation.mutateAsync({
        postcodeComponents: [postcodeToAdd],
      });
      onDismiss();
    } catch (error) {
      if (isPostcodeConflictError(error)) {
        // Store the postcode that conflicted so we can move it
        setMoveTarget({
          postcode: postcodeToAdd,
          fromZoneId: 0, // Will be determined by the backend
          fromZoneName: "", // Will be determined by the backend
        });
        setMoveDialogActive(true);
        return;
      }
      // If it's not an overlap error, let the error display
    }
  };

  const handleMoveComplete = () => {
    setMoveDialogActive(false);
    setMoveTarget(null);
    onDismiss();
  };

  const handleMoveDismiss = () => {
    setMoveDialogActive(false);
    setMoveTarget(null);
    // Stay in the zone selection dialog, user can choose a different zone
  };

  const handleMovePostcodes = async () => {
    if (!moveTarget) return;
    await movePostcodesMutation.mutateAsync({
      postcodeComponents: [moveTarget.postcode],
    });
    handleMoveComplete();
  };

  return (
    <>
      <Dialog active={active} onDismiss={onDismiss} fullHeight>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
          title={`The postcode ${postcode ?? ""} is not currently listed in a delivery zone`}
          description={
            "Choose a delivery zone from the list below to add this postcode to so that customers in this postcode see the correct delivery methods."
          }
        />
        <DialogContent className="flex flex-col gap-2">
          <Card className="flex flex-col gap-4 border border-zinc-300 bg-white p-4">
            {customerZones
              .filter(
                (zone) => !zone.default && zone.systemDefinedType === null,
              )
              .map((zone) => {
                const isSelected = selectedCustomerZoneId === zone.id;

                return (
                  <button
                    type="button"
                    key={zone.id}
                    className="flex w-full cursor-pointer gap-3"
                    onClick={() => setSelectedCustomerZoneId(zone.id)}
                  >
                    <ListItemIconTile
                      icon={isSelected ? "CHECK" : "MAP_MARKER"}
                    />
                    <div className="flex flex-col items-start">
                      <div className="font-semibold">{zone.name}</div>
                      <div className="text-xs text-zinc-600">
                        {[
                          zone.DrayDeliveryMethod
                            ? (zone.DrayDeliveryMethod.name ??
                              capitalize(
                                zone.DrayDeliveryMethod.fulfillmentType,
                              ))
                            : null,
                          zone.CourierDeliveryMethod
                            ? (zone.CourierDeliveryMethod.name ??
                              capitalize(
                                zone.CourierDeliveryMethod.fulfillmentType,
                              ))
                            : null,
                          zone.PalletDeliveryMethod
                            ? (zone.PalletDeliveryMethod.name ??
                              capitalize(
                                zone.PalletDeliveryMethod.fulfillmentType,
                              ))
                            : null,
                        ]
                          .filter(Boolean)
                          .join(", ") || "No delivery methods"}
                      </div>
                    </div>
                  </button>
                );
              })}
          </Card>

          {addCustomerZonePostcodesMutation.isError && !moveDialogActive && (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(addCustomerZonePostcodesMutation.error)
                  ?.response?.data.message ??
                "Unable to add postcode to delivery zone"
              }
            />
          )}
        </DialogContent>
        <DialogCTA>
          <Button color="secondary" onClick={() => navigate(DELIVERY_ZONES)}>
            Go to delivery zones
          </Button>
          <Button
            disabled={
              addCustomerZonePostcodesMutation.isPending ||
              selectedCustomerZoneId === null ||
              postcode === null
            }
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </DialogCTA>
      </Dialog>

      {/* Move Postcode Dialog */}
      {moveTarget && (
        <PostcodeMoveDialog
          active={moveDialogActive}
          onDismiss={handleMoveDismiss}
          postcodeCount={1}
          onMove={handleMovePostcodes}
          isMoving={movePostcodesMutation.isPending}
        />
      )}
    </>
  );
};
